import { useEffect } from "react";

const SITE = "WAAN METAL PTE. LTD.";
export const CANONICAL_DOMAIN = "https://www.renovationcontractor.com.sg";
const DEFAULT_IMAGE = `${CANONICAL_DOMAIN}/og-image.jpg`;

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SEO head manager.
 * @param {object} opts
 * @param {string} opts.title       Unique page title (brand appended automatically)
 * @param {string} opts.description Unique meta description
 * @param {string} [opts.keywords]  Comma-separated keywords (used sparingly)
 * @param {string} [opts.canonical] Full canonical URL override
 * @param {string} [opts.image]     Social share image URL
 * @param {boolean}[opts.noindex]   Set true for private/admin routes
 * @param {Array}  [opts.breadcrumbs] [{name, path}] for BreadcrumbList JSON-LD
 * @param {Array}  [opts.jsonLd]    Extra JSON-LD objects to inject
 */
export function useSEO({ title, description, keywords, canonical, image, noindex = false, breadcrumbs, jsonLd } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Renovation Contractor Singapore`;
    document.title = fullTitle;

    const canonicalHref = canonical || `${CANONICAL_DOMAIN}${window.location.pathname}`;
    const img = image || DEFAULT_IMAGE;

    upsertMeta("name", "description", description);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonicalHref);
    upsertMeta("property", "og:site_name", SITE);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:locale", "en_SG");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    upsertLink("canonical", canonicalHref);

    // Managed JSON-LD: clear previous, inject fresh
    document.head.querySelectorAll('script[data-seo-jsonld="1"]').forEach((n) => n.remove());
    const blocks = [];
    if (breadcrumbs && breadcrumbs.length) {
      blocks.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `${CANONICAL_DOMAIN}${b.path}`,
        })),
      });
    }
    if (jsonLd) blocks.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
    blocks.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
    });
  }, [title, description, keywords, canonical, image, noindex, JSON.stringify(breadcrumbs), JSON.stringify(jsonLd)]);
}

export function serviceJsonLd({ name, description, serviceType }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: serviceType || name,
    name,
    description,
    areaServed: { "@type": "Country", name: "Singapore" },
    provider: {
      "@type": "LocalBusiness",
      name: "WAAN METAL PTE. LTD.",
      "@id": `${CANONICAL_DOMAIN}/#organization`,
      url: CANONICAL_DOMAIN,
      address: {
        "@type": "PostalAddress",
        streetAddress: "1016 Geylang Ave 3, #03-173",
        postalCode: "389731",
        addressLocality: "Singapore",
        addressCountry: "SG",
      },
    },
  };
}
